/**
 * Essentiality Claim Chat API 实现
 */
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import type { PsHttpSSE } from '@patsnap/common-ps-http';
import type {
    IEssentialityClaimChatApi,
    ICreateClaimChatRequest,
    ICreateClaimChatResponse,
    ISendClaimMessageRequest,
    ISendClaimMessageResponse,
    IAnalyzeClaimRequest,
    IAnalyzeClaimResponse,
} from './essentiality-claim-chat-api';
import type { IEssentialityClaimSSEResponse } from '../types/essentiality-claim-types';

@injectable()
export class EssentialityClaimChatApiImpl extends PsHttpService implements IEssentialityClaimChatApi {
    private baseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka`;

    get options() {
        return {
            baseURL: this.baseURL,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    /**
     * 创建或获取聊天会话
     */
    async createChat(request: ICreateClaimChatRequest): Promise<ICreateClaimChatResponse> {
        const response = await this.post<ICreateClaimChatResponse>({
            url: '/api/essentiality-claim/chat/create',
            data: request,
            headers: {
                'X-PatSnap-From': 's-analytics-telecom',
            },
        });
        return response.data;
    }

    /**
     * 发送消息并开始对话
     */
    async sendMessage(request: ISendClaimMessageRequest): Promise<ISendClaimMessageResponse> {
        const response = await this.post<ISendClaimMessageResponse>({
            url: '/api/essentiality-claim/chat/message',
            data: request,
            headers: {
                'X-PatSnap-From': 's-analytics-telecom',
            },
        });
        return response.data;
    }

    /**
     * 获取 SSE 消息流
     */
    getMessageStream(chatId: string, lastMessageId?: string): PsHttpSSE<IEssentialityClaimSSEResponse> {
        const params: Record<string, string> = { chat_id: chatId };
        if (lastMessageId) {
            params.last_message_id = lastMessageId;
        }

        return this.sse<IEssentialityClaimSSEResponse>(
            '/api/essentiality-claim/chat/stream',
            params
        );
    }

    /**
     * 分析 Claim 的必要性
     */
    async analyzeClaim(request: IAnalyzeClaimRequest): Promise<IAnalyzeClaimResponse> {
        const response = await this.post<IAnalyzeClaimResponse>({
            url: '/api/essentiality-claim/analyze',
            data: request,
            headers: {
                'X-PatSnap-From': 's-analytics-telecom',
            },
        });
        return response.data;
    }

    /**
     * 停止当前分析任务
     */
    async stopAnalysis(chatId: string): Promise<{ success: boolean }> {
        const response = await this.post<{ success: boolean }>({
            url: '/api/essentiality-claim/chat/stop',
            data: { chat_id: chatId },
            headers: {
                'X-PatSnap-From': 's-analytics-telecom',
            },
        });
        return response.data;
    }
}
